import { omitBy, mapValues, isEmpty, isPlainObject } from 'lodash-es';

/**
 * Recursively removes null values and empty objects from a given object.
 *
 * This function traverses the input object and removes any properties that are null or empty objects.
 * It does not remove arrays or other types of values. Nested objects are also processed recursively.
 * Empty objects directly inside arrays are preserved (not removed).
 *
 * @template T - The type of the input object.
 * @param {T} obj - The object to process and remove null values and empty objects from.
 * @returns {Partial<T>} - A new object with null values and empty objects removed. The structure of the original object is preserved.
 *
 * @example
 * const input = {
 *   a: null,
 *   b: { c: null, d: 1 },
 *   e: [],
 *   f: ['text', empty x 2, undefined, 'more text'],
 *   g: [empty, undefined, { key: 'value' }]
 * };
 * const result = removeNullAndEmptyObjects(input);
 * // result: {
 * //   b: { d: 1 },
 * //   e: [],
 * //   f: ['text', '', '', '', 'more text'],
 * //   g: [{}, {}, { key: 'value' }]
 * // }
 */

export function removeNullAndEmptyObjects<T extends object>(
  obj: T,
): Partial<T> {
  /**
   * Helper function to process arrays by recursively handling nested objects
   * but preserving empty objects within arrays.
   */
  function processArray<A extends any[]>(arr: A): A {
    const hasStringElements = arr.some((item) => typeof item === 'string');
    // Check for real objects (not null), since typeof null is "object" in JavaScript
    const hasObjectElements = arr.some(
      (item) => typeof item === 'object' && item !== null,
    );
    // The spread operator [...arr] ensures array 'holes' (sparse array empty slots) are converted to undefined values
    // This is critical for maintaining array integrity when:
    // 1. Preserving positional consistency in translation arrays
    // 2. Ensuring the map() function processes every position, including empty slots
    // 3. Allowing the null/undefined handling below to properly replace these values based on array content type
    // Without this spread, array holes would remain holes and translations become misaligned
    return [...arr]
      .map((item) => {
        // If item is null or undefined, handle based on array content type
        if (item === null || item === undefined) {
          // Arrays with strings get empty strings
          if (hasStringElements) return '';
          // Arrays with objects (but no strings) get empty objects
          if (hasObjectElements) return {};
          // Default fallback for other types of arrays (numbers, booleans, etc.)
          return null;
        }
        if (isPlainObject(item)) {
          // Process object properties but don't remove empty objects in arrays
          const processed = removeNullAndEmptyObjects(item);
          return processed;
        }
        return item;
      })
      .filter((item): item is NonNullable<typeof item> => {
        // For arrays containing strings (like rich text arrays), preserve empty strings
        // to maintain positional consistency with translations
        if (hasStringElements && item === '') {
          return true;
        }

        // Otherwise, filter out null and undefined
        return item !== null && item !== undefined;
      }) as A;
  }

  // Handle array inputs
  if (Array.isArray(obj)) {
    return processArray(obj);
  }

  return omitBy(
    mapValues(obj, (value) => {
      if (value === null || value === undefined) return null;
      if (isPlainObject(value)) {
        const processed = removeNullAndEmptyObjects(value as object);
        return isEmpty(processed) ? null : processed;
      }
      if (Array.isArray(value)) {
        return processArray(value);
      }
      return value;
    }),
    (value) =>
      value === null ||
      value === undefined ||
      (isPlainObject(value) && isEmpty(value)),
  ) as Partial<T>;
}
